import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Box, Button, TextField, Alert, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, IconButton, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { swarmApi } from '../api';

const emptyRule = () => ({
  effect: 'deny',
  tool: '*',
  role: '*',
  environment: '*',
  maxRisk: '*',
  priority: 100,
  isEnabled: true,
  description: '',
  companyId: null,
});

function PolicyAdmin() {
  const [rules, setRules] = useState([]);
  const [env, setEnv] = useState('prod');
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, rule: emptyRule() });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [r, e] = await Promise.all([
        swarmApi.listPolicyRules(),
        swarmApi.getPolicyEnvironment().catch(() => ({ data: { environment: 'prod' } })),
      ]);
      setRules(r.data || []);
      setEnv(e.data?.environment || 'prod');
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load policy rules');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...dialog.rule, companyId: dialog.rule.companyId || null };
      if (dialog.rule.id) await swarmApi.updatePolicyRule(dialog.rule.id, body);
      else await swarmApi.createPolicyRule(body);
      setDialog({ open: false, rule: emptyRule() });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this policy rule?')) return;
    try {
      await swarmApi.deletePolicyRule(id);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Policy</Typography>
        <Chip label={`env: ${env}`} size="small" />
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialog({ open: true, rule: emptyRule() })}>Add rule</Button>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Higher priority wins. Deny beats allow at the same priority. Default seed may block <code>remove_service</code> in prod via Intent Console.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Priority</TableCell>
              <TableCell>Effect</TableCell>
              <TableCell>Tool</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Env</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Description</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.priority}</TableCell>
                <TableCell><Chip size="small" label={r.effect} color={r.effect === 'deny' ? 'error' : 'success'} /></TableCell>
                <TableCell>{r.tool}</TableCell>
                <TableCell>{r.role}</TableCell>
                <TableCell>{r.environment}</TableCell>
                <TableCell>{r.maxRisk}</TableCell>
                <TableCell>{r.isEnabled ? 'yes' : 'no'}</TableCell>
                <TableCell sx={{ maxWidth: 240 }}>{r.description}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => setDialog({ open: true, rule: { ...r } })}>Edit</Button>
                  <IconButton size="small" color="error" onClick={() => remove(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rules.length === 0 && (
              <TableRow><TableCell colSpan={9}><Typography color="text.secondary">No rules</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, rule: emptyRule() })} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.rule.id ? 'Edit rule' : 'New rule'}</DialogTitle>
        <DialogContent>
          <TextField select fullWidth margin="normal" label="Effect" value={dialog.rule.effect}
            onChange={(e) => setDialog(d => ({ ...d, rule: { ...d.rule, effect: e.target.value } }))}>
            <MenuItem value="deny">deny</MenuItem>
            <MenuItem value="allow">allow</MenuItem>
          </TextField>
          <TextField fullWidth margin="normal" label="Tool (* or name)" value={dialog.rule.tool}
            onChange={(e) => setDialog(d => ({ ...d, rule: { ...d.rule, tool: e.target.value } }))} />
          <TextField fullWidth margin="normal" label="Role (*|admin|viewer)" value={dialog.rule.role}
            onChange={(e) => setDialog(d => ({ ...d, rule: { ...d.rule, role: e.target.value } }))} />
          <TextField fullWidth margin="normal" label="Environment (*|prod|staging|dev)" value={dialog.rule.environment}
            onChange={(e) => setDialog(d => ({ ...d, rule: { ...d.rule, environment: e.target.value } }))} />
          <TextField fullWidth margin="normal" label="Max risk (*|low|medium|high)" value={dialog.rule.maxRisk}
            onChange={(e) => setDialog(d => ({ ...d, rule: { ...d.rule, maxRisk: e.target.value } }))} />
          <TextField fullWidth margin="normal" type="number" label="Priority" value={dialog.rule.priority}
            onChange={(e) => setDialog(d => ({ ...d, rule: { ...d.rule, priority: parseInt(e.target.value, 10) || 0 } }))} />
          <TextField fullWidth margin="normal" label="Description" value={dialog.rule.description || ''}
            onChange={(e) => setDialog(d => ({ ...d, rule: { ...d.rule, description: e.target.value } }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, rule: emptyRule() })}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PolicyAdmin;
